<script lang="ts">
	/**
	 * `/Account/Billing` — who the invoice is made out to, and what pays it (Design §13, §14).
	 *
	 * The address is not cosmetic. It is what the provider computes tax from, and an address that
	 * stops resolving to a jurisdiction stalls the next renewal invoice in `draft` — no
	 * finalisation, no payment attempt, no dunning, and access that quietly continues while nobody
	 * is charged (§9, tax stalls). So the server's validation message is surfaced verbatim rather
	 * than replaced with "could not save".
	 *
	 * Card details never touch this origin: "Update payment method" asks the server for a
	 * provider-hosted session and leaves for it. There is no card form here and there should never
	 * be one.
	 */

	import { onMount } from 'svelte';
	import { Button, Checkbox, Input } from 'flowbite-svelte';
	import { configurationStore } from '../../AppConfiguration';
	import {
		ConfirmButton,
		CountrySelect,
		EmptyState,
		FormField,
		PageHeader,
		Panel,
		SkeletonWrapper,
		TaxIDTypeSelect,
		BillingSkeleton,
		ListRowsPlaceholder
	} from '../../components';
	import { defaultTaxIDType, inferTaxIDType, taxIDExample, taxIDType, taxIDValueName } from '../../TaxIDTypes';
	import {
		createPaymentMethodSession,
		deletePaymentMethod,
		getAccountSummary,
		listPaymentMethods,
		setDefaultPaymentMethod,
		updateBilling
	} from '../../AppNetwork';
	import AbstractID from '../../AbstractID';
	import { toastError, toastSuccess } from '../../stores/toast';
	import type { AccountCustomer, AccountPaymentMethod, CustomerTaxID } from '../../AppTypes';

	let customer: AccountCustomer | null = null;
	let paymentMethods: AccountPaymentMethod[] = [];
	/**
	 * The cards load on their own, **guarded separately** from the page. They are a provider round
	 * trip — a whole Braintree customer per vault — while everything else on the page is local, so
	 * waiting for them held the billing form back behind the slowest read on it. A failure here
	 * costs the cards panel, never the address form.
	 */
	let methodsLoading: boolean = true;
	let methodsError: string = '';
	let loading: boolean = true;
	let loadError: string = '';
	let saving: boolean = false;
	let saveError: string = '';
	/** The payment method a mutation is running against, so only its own row shows as busy. */
	let busyUUID: string = '';
	let openingSession: boolean = false;

	// The working copy. Editing the fetched object directly would leave a failed save showing the
	// values the customer typed as though the server had accepted them.
	let draft = {
		billingName: '',
		billingCompany: '',
		isProfessional: false,
		billingEmail: '',
		line1: '',
		line2: '',
		city: '',
		state: '',
		postalCode: '',
		country: ''
	};
	let taxIDs: CustomerTaxID[] = [];

	$: professionalOffered =
		draft.country.length > 0 &&
		($configurationStore?.professionalCountries ?? []).includes(draft.country.toUpperCase()) &&
		draft.billingCompany.trim().length === 0;
	/**
	 * Which rows the customer has classified themselves, parallel to `taxIDs`.
	 *
	 * It cannot live on the row: `taxIDs` is what the save sends, and a bookkeeping flag inside it
	 * would travel to the API. The flag is what stops the inference below overwriting a deliberate
	 * choice — the same rule the checkout's prefill follows, seeding behind the customer's answer
	 * and never over it.
	 */
	let typeChosen: boolean[] = [];

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		// Started together, awaited apart: the form renders from the summary as soon as it lands.
		void fnLoadMethods();
		try {
			customer = (await getAccountSummary()).customer;
			fnResetDraft();
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnLoadMethods(): Promise<void> {
		methodsLoading = true;
		methodsError = '';
		try {
			paymentMethods = (await listPaymentMethods()).items;
		} catch (error) {
			methodsError = (error as Error).message;
		} finally {
			methodsLoading = false;
		}
	}

	function fnResetDraft(): void {
		if (customer === null) return;
		draft = {
			billingName: customer.billingName ?? '',
			billingCompany: customer.billingCompany ?? '',
			isProfessional: customer.isProfessional === true,
			billingEmail: customer.billingEmail ?? '',
			line1: customer.address?.line1 ?? '',
			line2: customer.address?.line2 ?? '',
			city: customer.address?.city ?? '',
			state: customer.address?.state ?? '',
			postalCode: customer.address?.postalCode ?? '',
			country: customer.address?.country ?? ''
		};
		/*
		 * One empty row where the customer has none, rather than a button that reveals one. Adding a
		 * tax ID was three actions before a number could be typed — press Add, classify the document,
		 * then type — and the first of them revealed a field rather than doing anything. A single
		 * empty pair is what the section is for, and a blank row is dropped at save.
		 */
		taxIDs = customer.taxIDs.length > 0
			? customer.taxIDs.map(taxID => ({ ...taxID }))
			: [{ type: defaultTaxIDType(customer.address?.country ?? ''), value: '' }];
		// Every row that came from the server carries a type its owner chose; only a blank new one
		// is still open to being read off the number.
		typeChosen = taxIDs.map(taxID => taxID.value.trim().length > 0);
	}

	onMount(fnLoad);

	/** Empty means "not set" to this API, so a blank field is sent as null rather than as "". */
	function orNull(value: string): string | null {
		const trimmed = value.trim();
		return trimmed.length === 0 ? null : trimmed;
	}

	async function fnSave(): Promise<void> {
		saving = true;
		saveError = '';
		try {
			customer = await updateBilling({
				billingName: orNull(draft.billingName),
				// Null clears it, which is the only way to stop being billed as a business — so a
				// cleared box has to reach the server as null rather than be omitted.
				billingCompany: orNull(draft.billingCompany),
				isProfessional: draft.isProfessional,
				billingEmail: orNull(draft.billingEmail),
				address: {
					line1: orNull(draft.line1),
					line2: orNull(draft.line2),
					city: orNull(draft.city),
					state: orNull(draft.state),
					postalCode: orNull(draft.postalCode),
					country: orNull(draft.country)
				},
				// Blank rows are dropped rather than refused: a half-typed row the customer abandoned
				// is not an error worth blocking a save over.
				taxIDs: taxIDs.filter(taxID => taxID.type.trim().length > 0 && taxID.value.trim().length > 0)
			});
			fnResetDraft();
			toastSuccess('Your billing details have been saved.');
		} catch (error) {
			saveError = (error as Error).message;
		} finally {
			saving = false;
		}
	}

	/**
	 * Tax IDs are pairs (`eu_vat`, `DE123456789`), which is why they are not a `StringListEditor`:
	 * that component edits a list of single strings, and squeezing a pair into one would invent an
	 * encoding the API knows nothing about.
	 *
	 * A new row opens on the likeliest document for the country already in the address above,
	 * because that is what almost everybody adding one is holding — offered and changeable, never
	 * demanded. With no country there is no guess to make, and the row opens blank rather than on
	 * whichever type sorts first.
	 */
	function fnAddTaxID(): void {
		taxIDs = [...taxIDs, { type: defaultTaxIDType(draft.country), value: '' }];
		typeChosen = [...typeChosen, false];
	}

	function fnRemoveTaxID(index: number): void {
		taxIDs = taxIDs.filter((_, position) => position !== index);
		typeChosen = typeChosen.filter((_, position) => position !== index);
	}

	/**
	 * Reads the document off the number as it is typed.
	 *
	 * Only while the customer has not chosen the type themselves, and only when the number actually
	 * names a jurisdiction — so it fills a field nobody has answered and never argues with one
	 * somebody has. A wrong guess is visible in the control beside it and costs one dropdown.
	 */
	function fnNumberTyped(index: number): void {
		if (typeChosen[index] === true) return;

		const inferred = inferTaxIDType(taxIDs[index].value);
		if (inferred === '' || inferred === taxIDs[index].type) return;

		taxIDs[index].type = inferred;
		taxIDs = taxIDs;
	}

	/** A type the customer picked is theirs, and nothing typed afterwards may move it. */
	function fnTypeChosen(index: number): void {
		typeChosen[index] = true;
		typeChosen = typeChosen;
	}

	/*
	 * A row whose type is not one the provider accepts is flagged **on that row** — see the markup.
	 *
	 * They exist because the type used to be a free text box: `VAT`, `vat`, `DE` were all saved
	 * here and all refused by the provider afterwards, so the store holds identifiers it has never
	 * been able to send. The row is shown, flagged, and left selectable rather than quietly
	 * rewritten — but the save is **not** blocked on it. The server refuses an unsendable type by
	 * name, which is the authority; a gate here would lock a customer out of correcting their own
	 * address the day this list falls behind the provider's.
	 */

	async function fnUpdatePaymentMethod(): Promise<void> {
		openingSession = true;
		try {
			// Back to this page, so a customer who has just fixed their card lands where the change
			// is visible — and where the server's `payInvoice` on update (§9.5) has already run.
			const session = await createPaymentMethodSession(window.location.href);
			AbstractID.navigateTo(session.url);
		} catch (error) {
			toastError('The payment method page could not be opened.', (error as Error).message);
		} finally {
			openingSession = false;
		}
	}

	async function fnMakeDefault(method: AccountPaymentMethod): Promise<void> {
		busyUUID = method.uuid;
		try {
			// The endpoint answers with the whole list rather than the promoted row: promoting one
			// card clears another, and only the server knows which. Patching a single row here
			// would leave two cards both claiming to be the default.
			paymentMethods = (await setDefaultPaymentMethod(method.uuid)).items;
			toastSuccess('That card will be used from now on.');
		} catch (error) {
			toastError('The default payment method was not changed.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	async function fnRemove(method: AccountPaymentMethod): Promise<void> {
		busyUUID = method.uuid;
		try {
			await deletePaymentMethod(method.uuid);
			paymentMethods = paymentMethods.filter(row => row.uuid !== method.uuid);
			toastSuccess('That payment method has been removed.');
		} catch (error) {
			toastError('The payment method was not removed.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	function methodLine(method: AccountPaymentMethod): string {
		const brand = method.brand ?? method.type ?? 'Card';
		const tail = method.last4 === null ? '' : ` •••• ${method.last4}`;
		return `${brand}${tail}`;
	}

	function expiryLine(method: AccountPaymentMethod): string {
		if (method.expiryMonth === null || method.expiryYear === null) return '';
		return `Expires ${String(method.expiryMonth).padStart(2, '0')}/${method.expiryYear}`;
	}
</script>

<PageHeader title="Billing">
	<svelte:fragment slot="description">
		Where invoices are addressed, and what pays them.
	</svelte:fragment>
</PageHeader>

{#if loading}
	<SkeletonWrapper>
		<BillingSkeleton />
	</SkeletonWrapper>
{:else if loadError.length > 0}
	<EmptyState variant="error" title="Your billing details could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
	</EmptyState>
{:else}
	<div class="grid gap-6 lg:grid-cols-3 items-start">
		<div class="lg:col-span-2">
			<Panel title="Billing details" faIcon="address-card">
				<form class="space-y-4" on:submit|preventDefault={fnSave}>
					<div class="grid gap-4 sm:grid-cols-2">
						<FormField label="Name on the invoice" id="billingName">
							<Input id="billingName" bind:value={draft.billingName} placeholder="Your name" />
						</FormField>
						<!--
							Two things ride on this, and the helper says both: a named company is
							what the invoice is addressed to instead of the name above, and it is
							what makes the sale a business sale for the tax rate (§10.1).
						-->
						<FormField
							label="Company"
							id="billingCompany"
							description="If you're buying for a company, invoices are billed to it and business tax rates apply."
						>
							<Input
								id="billingCompany"
								bind:value={draft.billingCompany}
								placeholder="Company name"
								autocomplete="organization"
							/>
						</FormField>
						<!--
							§10.1 — only where the billing country takes professionals as businesses and
							no company is named, which are the only cases it changes anything; a box that
							did nothing would be a claim the store does not honour.
						-->
						{#if professionalOffered}
							<label class="flex items-start gap-2 text-sm sm:col-span-2">
								<Checkbox spacing="" class="shrink-0 mt-0.5" bind:checked={draft.isProfessional} />
								<span>
									I buy for my work as a sole trader or freelancer, without a company.
									<span class="block text-xs text-default">Business tax rates apply to your purchases.</span>
								</span>
							</label>
						{/if}
						<FormField
							label="Invoices go to"
							id="billingEmail"
							description="Leave blank to use your account email."
						>
							<Input id="billingEmail" type="email" bind:value={draft.billingEmail} />
						</FormField>
					</div>

					<FormField
						label="Address"
						id="line1"
						helpText="Your address decides which tax applies. If it stops resolving to a country, invoices stop being issued, so keep it current."
					>
						<Input id="line1" bind:value={draft.line1} placeholder="Street address" />
					</FormField>
					<FormField id="line2">
						<Input id="line2" bind:value={draft.line2} placeholder="Apartment, suite, etc." />
					</FormField>

					<div class="grid gap-4 sm:grid-cols-2">
						<FormField label="City" id="city">
							<Input id="city" bind:value={draft.city} />
						</FormField>
						<FormField label="State or province" id="state">
							<Input id="state" bind:value={draft.state} />
						</FormField>
						<FormField label="Postal code" id="postalCode">
							<Input id="postalCode" bind:value={draft.postalCode} />
						</FormField>
						<FormField label="Country" id="country" description="What decides the tax on your invoices.">
							<!-- Clearable, unlike checkout's: a billing address is editable and a customer
							     who wants theirs removed must be able to say so rather than being stuck
							     with whichever country they last picked. -->
							<CountrySelect id="country" bind:value={draft.country} allowEmpty emptyLabel="Not set" />
						</FormField>
					</div>

					<FormField
						label="Tax IDs"
						description="Added to your invoices where the tax rules require it. A VAT number registered in the EU is also what stops the tax being charged to you."
					>
						<div class="space-y-3">
							{#each taxIDs as taxID, index (index)}
								{@const unsendable = taxID.type.trim().length > 0 && taxIDType(taxID.type) === null}
								{@const removable = taxIDs.length > 1 || taxID.value.trim().length > 0}
								<div class="space-y-1">
									<!--
										The **number leads**, because it is the half the customer is holding:
										they know their VAT number, they do not know it is spelled `eu_vat`.
										The type follows and is read off the number where the number names a
										jurisdiction, so the common case is one field. It stays a control
										rather than a derived caption — the guess can be wrong, and the whole
										point of a closed set is that what is typed never becomes the value.

										The placeholder is that type's own specimen: what an EU VAT number
										looks like says more than the word "Number" does.
									-->
									<!--
										A grid with the three cells placed explicitly, not `flex-wrap`.
										Wrapping put the remove button alone on a line of its own the moment
										the row was too narrow for all three — a stray bordered button under
										two fields, which reads as a control belonging to nothing. Here the
										number and the button share the top row at every width and only the
										type drops beneath them, so the button stays attached to the row it
										removes. Both templates are whole literal classes: a class built at
										runtime is invisible to the JIT scanner and silently never ships.
									-->
									<div
										class="grid gap-2 items-center {removable
											? 'grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_16rem_auto]'
											: 'grid-cols-1 sm:grid-cols-[minmax(0,1fr)_16rem]'}"
									>
										<Input
											class="w-full"
											bind:value={taxID.value}
											on:input={() => fnNumberTyped(index)}
											placeholder={taxIDExample(taxID.type) === '' ? 'Tax ID number' : taxIDExample(taxID.type)}
											aria-label="Tax ID number"
										/>
										<TaxIDTypeSelect
											class="w-full col-span-full row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1"
											bind:value={taxID.type}
											country={draft.country}
											on:change={() => fnTypeChosen(index)}
											aria-label="Tax ID type"
										/>
										<!--
											Rendered only when it would do something. The row offered by
											default is empty, and an empty row is already dropped at save —
											so a button to remove it deletes nothing and asks the customer
											to think about a control that is pure decoration.

											`color="none"` + `!p-2.5` strips flowbite's chrome and matches
											the field height: a bordered button beside two fields is given
											the same visual weight as the things it sits between, and the
											weight belongs to the fields.
										-->
										{#if removable}
											<Button
												color="none"
												class="!p-2.5 justify-self-end row-start-1 col-start-2 sm:col-start-3 rounded-lg text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-500"
												title="Remove this tax ID"
												aria-label={taxID.value.trim().length > 0
													? `Remove ${taxID.value.trim()}`
													: 'Remove this tax ID'}
												on:click={() => fnRemoveTaxID(index)}
											>
												<i class="fa-solid fa-xmark" />
											</Button>
										{/if}
									</div>
									<!--
										Beside the row it is about, not in a tally underneath: a count at the
										bottom of a list of five says something is wrong and not which one.
									-->
									{#if unsendable}
										<p class="text-sm text-amber-600 dark:text-amber-500">
											This store cannot send <span class="font-medium">{taxID.type}</span> to the
											payment provider. Choose a type, or remove the row, and save again.
										</p>
									{:else if taxID.type.trim().length > 0 && typeChosen[index] !== true && taxID.value.trim().length > 0}
										<!-- Says what was read off the number, so a wrong guess is caught here
										     rather than on an invoice made out under the wrong scheme. -->
										<p class="text-sm text-default">
											Read as {taxIDValueName(taxID.type)}. Change it above if that is wrong.
										</p>
									{/if}
								</div>
							{/each}
							<Button color="alternative" size="sm" on:click={fnAddTaxID}>
								<i class="fa-solid fa-plus mr-2" />{taxIDs.length === 0
									? 'Add a tax ID'
									: 'Add another tax ID'}
							</Button>
						</div>
					</FormField>

					{#if saveError.length > 0}
						<p class="text-sm text-red-600 dark:text-red-500">{saveError}</p>
					{/if}

					<div class="flex gap-2">
						<Button type="submit" color="primary" disabled={saving}>Save billing details</Button>
						<Button color="alternative" disabled={saving} on:click={fnResetDraft}>Discard changes</Button>
					</div>
				</form>
			</Panel>
		</div>

		<div class="space-y-6">
			<Panel title="Payment methods" faIcon="credit-card">
				{#if methodsLoading}
					<ListRowsPlaceholder rows={2} />
				{:else if methodsError.length > 0}
					<EmptyState compact variant="error" title="Your cards could not be loaded." description={methodsError}>
						<Button color="alternative" size="sm" on:click={() => void fnLoadMethods()}>Try again</Button>
					</EmptyState>
				{:else if paymentMethods.length === 0}
					<EmptyState compact title="No card on file." />
				{:else}
					<ul class="space-y-2">
						{#each paymentMethods as method (method.uuid)}
							<li class="widget bg-depth space-y-2">
								<div class="flex flex-wrap items-center gap-2">
									<span class="font-semibold">{methodLine(method)}</span>
									{#if method.isDefault}
										<!-- Words, not a highlight: which card is charged is not a decoration. -->
										<span class="text-xs font-semibold uppercase tracking-wider text-green-600 dark:text-green-400">
											Used by default
										</span>
									{/if}
								</div>
								{#if expiryLine(method).length > 0}
									<div class="text-sm text-default">{expiryLine(method)}</div>
								{/if}
								<div class="flex flex-wrap gap-2">
									{#if !method.isDefault}
										<Button
											color="alternative"
											size="xs"
											disabled={busyUUID === method.uuid}
											on:click={() => void fnMakeDefault(method)}
										>
											Use this one
										</Button>
									{/if}
									<ConfirmButton
										color="red"
										size="xs"
										disabled={busyUUID === method.uuid}
										action={`remove ${methodLine(method)}`}
										on:confirm={() => void fnRemove(method)}
									>
										Remove
									</ConfirmButton>
								</div>
							</li>
						{/each}
					</ul>
				{/if}

				<Button
					color="primary"
					size="sm"
					class="w-full mt-4"
					disabled={openingSession}
					on:click={fnUpdatePaymentMethod}
				>
					Update payment method
				</Button>
				<!-- Centred under the full-width button it explains, rather than ranged left against
				     a control that is not: it is a caption on that button, not a paragraph of the
				     panel. -->
				<p class="text-xs text-default mt-2 text-center">
					Card details are entered on our payment provider's own page. They never reach this site.
				</p>
			</Panel>

			{#if customer?.currency}
				<Panel title="Currency" faIcon="coins" compact>
					<p class="text-sm">
						You are billed in {customer.currency.toUpperCase()}, set by your first purchase.
					</p>
				</Panel>
			{/if}
		</div>
	</div>
{/if}
